<template>
  <div>
    <q-table
      flat
      bordered
      dense
      :rows="rankedBrokerSummaryData"
      :columns="columns"
      :pagination="pagination"
      :filter="filterBroker"
      :filter-method="filterBrokerData"
      binary-state-sort
      separator="cell"
      :class="
        $q.dark.mode
          ? 'table-sticky-header-column-dark'
          : 'table-sticky-header-column'
      "
    >
      <template v-slot:top-left>
        <div class="row">
          <span>
            <q-input
              borderless
              dense
              debounce="300"
              v-model="filterBroker"
              placeholder="Filter Broker"
            >
              <template v-slot:append>
                <q-icon name="search" />
              </template>
            </q-input>
          </span>
        </div>
      </template>
      <template v-slot:body-cell-buyer="props">
        <q-td :props="props">
          <q-item
            class="row justify-center"
            dense
            clickable
            :to="{
              path: '/brokers-analytics-pro',
              query: { brokerID: props.value, transaction: 'Purchase' },
            }"
          >
            <div class="col">
              {{ props.value }}
            </div>
          </q-item>
        </q-td>
      </template>

      <template v-slot:body-cell-buy_amount="props">
        <q-td :props="props" align="center">
          <q-linear-progress
            size="25px"
            stripe
            :value="
              getSingleAmountPercentage(props.row?.buy_amount, totalBuyAmount)
            "
            color="secondary"
            class="q-mt-sm"
          >
            <div class="absolute-full flex flex-center">
              <q-badge
                color="white"
                text-color="secondary"
                :label="formatAmountData(props.row?.buy_amount, totalBuyAmount)"
              />
            </div>
          </q-linear-progress>
        </q-td>
      </template>

      <template v-slot:body-cell-sell_amount="props">
        <q-td :props="props" align="center">
          <q-linear-progress
            size="25px"
            stripe
            :value="
              getSingleAmountPercentage(props.row?.sell_amount, totalSellAmount)
            "
            color="red-7"
            class="q-mt-sm"
          >
            <div class="absolute-full flex flex-center">
              <q-badge
                color="white"
                text-color="red-7"
                :label="
                  formatAmountData(props.row?.sell_amount, totalSellAmount)
                "
              />
            </div>
          </q-linear-progress>
        </q-td>
      </template>

      <template v-slot:body-cell-total="props">
        <q-td :props="props" align="center">
          <q-linear-progress
            size="25px"
            stripe
            :value="
              getSingleAmountPercentage(
                props.row?.total,
                totalBuyAmount + totalSellAmount
              )
            "
            color="primary"
            class="q-mt-sm"
          >
            <div class="absolute-full flex flex-center">
              <q-badge
                color="white"
                text-color="primary"
                :label="
                  formatAmountData(
                    props.row?.total,
                    totalBuyAmount + totalSellAmount
                  )
                "
              />
            </div>
          </q-linear-progress>
        </q-td>
      </template>

      <template v-slot:top-row>
        <q-tr
          class="text-bold"
          :class="$q.dark.mode ? 'bg-grey-9' : ' bg-green-1'"
        >
          <q-td class="text-left text-caption" colspan="1">Total</q-td>
          <q-td class="text-center"> - </q-td>
          <q-td class="text-center">
            {{ totalBuyAmount?.toFixed(2) }}
          </q-td>
          <q-td class="text-center">
            {{ totalSellAmount?.toFixed(2) }}
          </q-td>
          <q-td class="text-center">
            {{ (totalBuyAmount + totalSellAmount).toFixed(2) }}
          </q-td>
          <q-td class="text-center">
            {{ totalNetAmount?.toFixed(2) }}
          </q-td>
          <q-td class="text-center"> - </q-td>
        </q-tr>
      </template>
    </q-table>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import useData from "src/use/data";
import useUtils from "src/use/utils";
import { useQuasar } from "quasar";

const $q = useQuasar();
const { brokerSummary } = useData();
const { amountConversion } = useUtils();

const brokerSummaryData = ref([]);
const totalBuyAmount = ref(null);
const totalSellAmount = ref(null);
const totalNetAmount = ref(null);

brokerSummary().then((res) => {
  const resData = res.data;

  totalBuyAmount.value = resData.reduce(
    (total, item) => total + item.buy_amount,
    0
  );
  totalSellAmount.value = resData.reduce(
    (total, item) => total + item.sell_amount,
    0
  );
  totalNetAmount.value = resData.reduce(
    (total, item) => total + item.net_amount,
    0
  );

  brokerSummaryData.value = resData.map((item) => {
    let totalBuySell = item.buy_amount + item.sell_amount;

    return {
      ...item,
      total: totalBuySell,
    };
  });
});

const columns = [
  {
    name: "rank",
    label: "Rank",
    field: "rank",
    sortable: true,
    align: "left",
  },
  {
    name: "buyer",
    label: "Buyer",
    field: "buyer",
    sortable: true,
    align: "center",
  },
  {
    name: "buy_amount",
    label: "Buy Amount",
    field: "buy_amount",

    sortable: true,
    align: "center",
  },
  {
    name: "sell_amount",
    label: "Sell Amount",
    field: "sell_amount",

    sortable: true,
    align: "center",
  },
  {
    name: "total",
    label: "Total",
    field: "total",

    sortable: true,
    align: "center",
  },
  {
    name: "net_amount",
    label: "Net Holding",
    field: "net_amount",
    // format: (val, row) => {
    //   const netAmount = parseFloat(row.net_amount)?.toFixed(2);
    //   const percentage = parseFloat(
    //     (row.net_amount / totalNetAmount.value) * 100
    //   )?.toFixed(2);
    //   return `${netAmount}  ( ${percentage} % )`;
    // },
    sortable: true,
    align: "center",
  },

  {
    name: "net_matching",
    label: "Net Matching",
    field: "net_matching",
    sortable: true,
    align: "center",
  },
];

const pagination = ref({
  rowsPerPage: 100,
  sortBy: "total",
  descending: true,
});

const filterBroker = ref("");
const filterBrokerData = (rows, terms, cols, getCellValue) => {
  return rows.filter((row) => {
    return row.buyer.toLowerCase().includes(filterBroker.value.toLowerCase());
  });
};

const formatAmountData = (singlAmt, totAmt) => {
  const singleAmount = parseFloat(singlAmt);
  const percentage = parseFloat((singlAmt / totAmt) * 100)?.toFixed(2);
  return `${amountConversion(singleAmount)}  (${percentage} %)`;
};

const getSingleAmountPercentage = (singlAmt, totAmt) => {
  return singlAmt / totAmt;
};

const rankedBrokerSummaryData = computed(() =>
  brokerSummaryData.value
    .slice()
    .sort((a, b) => b.total - a.total)
    .map((row, index) => ({
      ...row,
      rank: index + 1,
    }))
);
</script>
